import { Box, Stack, Typography, styled } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { type KeyboardEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import {
  KNOWLEDGE_HISTORY_LIMIT,
  useKnowledgeConversationsQuery,
} from '@/features/internal/knowledge'
import {
  FONT_WEIGHT,
  ICON_SIZE,
  scaledFontSize,
} from '@/shared/styles/typography'

/**
 * sidebar IconBar active='knowledge' 時の context pane = 過去の会話。
 *
 * 制度の照会は同じ論点が周期的に戻ってくる (「前に聞いたやつ、どうだったっけ」)。
 * 一覧を持たないと聞き直しになり、問い合わせを減らす目的と噛み合わない。
 */

// 文字サイズモード追従 (ADR-0019): サイドバーに載る文字は幅と一緒に拡大する。
const Section = styled(Box)(({ theme }) => ({
  padding: '8px 12px',
  fontSize: scaledFontSize('bodySm')(theme),
  color: colorTokens.fg.default,
}))

const Heading = styled(Typography)(({ theme }) => ({
  color: colorTokens.fg.muted,
  fontSize: scaledFontSize('tag')(theme),
  fontWeight: FONT_WEIGHT.medium,
  marginBottom: 8,
}))

// empty-states 型 1 (明示プレースホルダ)。italic は入力促し (型 4) 専用なので使わない。
// 一覧の終端 (上限に達した旨) も同じ体裁で置く — 押せない事実の行という役が同じ。
const EmptyRow = styled(Box)(({ theme }) => ({
  padding: '5px 8px',
  fontSize: scaledFontSize('tag')(theme),
  color: colorTokens.fg.muted,
}))

// 一覧は back が上限までしか返さない。溢れたことを書かないと、古い質問が消えたのか
// 自分の記憶違いなのかを利用者が区別できない (黙って切らない)。
const TRUNCATED_LABEL = 'これより古い質問は表示していません'

const Row = styled(Box, {
  shouldForwardProp: (p) => p !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  padding: '5px 8px',
  borderRadius: RADIUS.sm,
  cursor: 'pointer',
  fontSize: scaledFontSize('tag')(theme),
  color: active ? colorTokens.fg.default : colorTokens.fg.muted,
  // 選択中 = ピル状アクティブ行 (warm gray, 青背景にしない)
  background: active ? colorTokens.bg.muted : 'transparent',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:hover': active ? undefined : { background: colorTokens.bg.muted },
}))

interface Props {
  onNavigate?: () => void
}

export const KnowledgePane = ({ onNavigate }: Props) => {
  const navigate = useNavigate()
  const { conversationId } = useParams<{ conversationId?: string }>()
  const { data: conversations } = useKnowledgeConversationsQuery()

  const go = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  // Row は div なので button の役を明示する。行ごとに書くと付け忘れが出る
  // (「新しい質問」だけ Tab で辿れない状態になっていた)。
  const rowProps = (path: string) => ({
    role: 'button',
    tabIndex: 0,
    onClick: () => go(path),
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      e.preventDefault()
      go(path)
    },
  })

  return (
    <Section sx={{ pt: 1.5 }}>
      <Row active={!conversationId} {...rowProps('/knowledge')}>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <AddIcon sx={{ fontSize: ICON_SIZE.sm }} />
          新しい質問
        </Stack>
      </Row>

      <Heading sx={{ mt: 2 }}>過去の質問</Heading>
      {conversations && conversations.length > 0 ? (
        <Stack spacing={0.25}>
          {conversations.map((conversation) => (
            <Row
              key={conversation.id}
              active={conversation.id === conversationId}
              title={conversation.title}
              {...rowProps(`/knowledge/${conversation.id}`)}
            >
              {conversation.title}
            </Row>
          ))}
          {conversations.length >= KNOWLEDGE_HISTORY_LIMIT ? (
            <EmptyRow>{TRUNCATED_LABEL}</EmptyRow>
          ) : null}
        </Stack>
      ) : (
        <EmptyRow>(なし)</EmptyRow>
      )}
    </Section>
  )
}
